/**
 * LiveRoom — TikTok/Bigo-style live: WebRTC mesh video boxes (1-12), viewers,
 * chat, host background/layout controls, screen share. No gifts — just fun.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { Radio, Mic, MicOff, Video, VideoOff, MonitorUp, LogOut, Eye, Send, Palette, LayoutGrid, X, Share2, FlipHorizontal2, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuthStore } from '../store/authStore';
import { toast } from 'sonner';
import { BG_PRESETS } from './Live';

const ICE = { iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] };
const gridCols = (n) => (n <= 1 ? 1 : n <= 4 ? 2 : n <= 9 ? 3 : 4);

const VideoTile = ({ stream, muted, label, isHost, mirrored }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && stream) ref.current.srcObject = stream;
  }, [stream]);
  return (
    <div className="relative w-full h-full">
      {stream ? (
        <video ref={ref} autoPlay playsInline muted={muted} className="w-full h-full object-cover rounded-lg bg-black" style={{ transform: mirrored ? 'scaleX(-1)' : 'none' }} />
      ) : (
        <div className="w-full h-full rounded-lg bg-black/50 flex items-center justify-center">
          <VideoOff className="w-6 h-6 text-white/30" />
        </div>
      )}
      <span className={`absolute bottom-1.5 left-1.5 text-[11px] px-2 py-0.5 rounded-full ${isHost ? 'bg-red-600 text-white' : 'bg-black/60 text-white/90'}`}>
        {isHost ? '★ ' : ''}{label}
      </span>
    </div>
  );
};

export default function LiveRoom() {
  const { roomId: urlRoomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const myName = user?.name || 'Guest';

  const [roomState, setRoomState] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [remoteStreams, setRemoteStreams] = useState({});
  const [localStream, setLocalStream] = useState(null);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [showBgPicker, setShowBgPicker] = useState(false);
  const [floaters, setFloaters] = useState([]);
  const [hypeLevel, setHypeLevel] = useState(0);
  const [mirrored, setMirrored] = useState(false);

  const socketRef = useRef(null);
  const peersRef = useRef({});
  const localStreamRef = useRef(null);
  const roomIdRef = useRef(null);
  const mySidRef = useRef(null);
  const roomStateRef = useRef(null);
  const autoPublishRef = useRef(false);
  const chatEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const prevMsgCountRef = useRef(0);
  const [unseenChat, setUnseenChat] = useState(0);
  const reactionTimesRef = useRef([]);

  const updateHype = () => {
    const now = Date.now();
    reactionTimesRef.current = reactionTimesRef.current.filter((t) => now - t < 5000);
    const n = reactionTimesRef.current.length;
    setHypeLevel(n >= 14 ? 2 : n >= 6 ? 1 : 0);
  };

  const emit = (ev, data) => socketRef.current?.emit(ev, { room_id: roomIdRef.current, ...data });

  const createPeer = useCallback((peerSid) => {
    const pc = new RTCPeerConnection(ICE);
    const state = { makingOffer: false, ignoreOffer: false, polite: (mySidRef.current || '') < peerSid };
    pc.onnegotiationneeded = async () => {
      try {
        state.makingOffer = true;
        await pc.setLocalDescription();
        emit('live_signal', { target: peerSid, data: { sdp: pc.localDescription } });
      } catch (e) { /* ignore */ } finally { state.makingOffer = false; }
    };
    pc.onicecandidate = (e) => { if (e.candidate) emit('live_signal', { target: peerSid, data: { candidate: e.candidate } }); };
    pc.ontrack = (e) => setRemoteStreams((prev) => ({ ...prev, [peerSid]: e.streams[0] }));
    if (localStreamRef.current) localStreamRef.current.getTracks().forEach((t) => pc.addTrack(t, localStreamRef.current));
    peersRef.current[peerSid] = { pc, state };
    return peersRef.current[peerSid];
  }, []);

  const closePeer = (sid) => {
    peersRef.current[sid]?.pc.close();
    delete peersRef.current[sid];
    setRemoteStreams((prev) => { const n = { ...prev }; delete n[sid]; return n; });
  };

  const handleSignal = useCallback(async ({ from, data }) => {
    const entry = peersRef.current[from] || createPeer(from);
    const { pc, state } = entry;
    try {
      if (data?.sdp) {
        const collision = data.sdp.type === 'offer' && (state.makingOffer || pc.signalingState !== 'stable');
        state.ignoreOffer = !state.polite && collision;
        if (state.ignoreOffer) return;
        await pc.setRemoteDescription(data.sdp);
        if (data.sdp.type === 'offer') {
          await pc.setLocalDescription();
          emit('live_signal', { target: from, data: { sdp: pc.localDescription } });
        }
      } else if (data?.candidate) {
        try { await pc.addIceCandidate(data.candidate); } catch (e) { if (!state.ignoreOffer) console.warn(e); }
      }
    } catch (e) { console.warn('live signal error', e); }
  }, [createPeer]);

  const publishStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      localStreamRef.current = stream;
      setLocalStream(stream);
      setMicOn(true); setCamOn(true);
      Object.entries(peersRef.current).forEach(([, { pc }]) => stream.getTracks().forEach((t) => pc.addTrack(t, stream)));
      // peers for members without connections yet
      ((roomStateRef.current || roomState)?.members || []).forEach((m) => { if (m.sid !== mySidRef.current && !peersRef.current[m.sid]) createPeer(m.sid); });
      return true;
    } catch (e) {
      toast.error('Camera/mic access denied');
      return false;
    }
  };

  const unpublishStream = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    Object.values(peersRef.current).forEach(({ pc }) => pc.getSenders().forEach((s) => { try { pc.removeTrack(s); } catch (e) {} }));
    localStreamRef.current = null;
    setLocalStream(null);
    setSharing(false);
  };

  useEffect(() => {
    // Host reclaimed the room (came back after leaving) — the camera was never
    // re-published, leaving every box black. Auto-republish once.
    if (roomState && roomState.host_sid === mySidRef.current && !localStreamRef.current && !autoPublishRef.current) {
      autoPublishRef.current = true;
      publishStream();
    }
  }, [roomState]); // eslint-disable-line

  useEffect(() => {
    const socket = io(process.env.REACT_APP_BACKEND_URL, { path: '/api/socket.io', transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', async () => {
      mySidRef.current = socket.id;
      const create = location.state?.create;
      if (urlRoomId === 'new' && create) {
        socket.emit('live_start', { name: myName, user_id: user?.user_id, ...create });
      } else if (urlRoomId !== 'new') {
        roomIdRef.current = urlRoomId;
        socket.emit('live_join', { room_id: urlRoomId, name: myName, user_id: user?.user_id });
      } else {
        navigate('/live');
      }
    });
    socket.on('live_started', async ({ room_id }) => {
      roomIdRef.current = room_id;
      window.history.replaceState(null, '', `/live/${room_id}`);
      await publishStream(); // host is box 0
    });
    socket.on('live_state', (state) => {
      roomStateRef.current = state;
      setRoomState(state);
      // If we're publishing (host or box member), make sure every member has a peer
      // — covers rejoining after leaving and socket reconnects.
      if (localStreamRef.current) {
        (state?.members || []).forEach((m) => {
          if (m.sid !== mySidRef.current && !peersRef.current[m.sid]) createPeer(m.sid);
        });
      }
    });
    socket.on('live_member_joined', (m) => {
      setRoomState((prev) => prev && { ...prev, members: [...prev.members.filter((x) => x.sid !== m.sid), m], viewer_count: prev.viewer_count + 1 });
      if (localStreamRef.current && m.sid !== mySidRef.current && !peersRef.current[m.sid]) createPeer(m.sid);
    });
    socket.on('live_member_left', ({ sid }) => {
      closePeer(sid);
      setRoomState((prev) => prev && { ...prev, members: prev.members.filter((x) => x.sid !== sid) });
    });
    socket.on('live_box_update', ({ sid, box }) => {
      setRoomState((prev) => prev && { ...prev, members: prev.members.map((m) => (m.sid === sid ? { ...m, box } : m)) });
    });
    socket.on('live_background', ({ background }) => setRoomState((prev) => prev && { ...prev, background }));
    socket.on('live_chat', (msg) => setMessages((prev) => [...prev.slice(-99), msg]));
    socket.on('live_reaction', ({ emoji }) => {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      setFloaters((prev) => [...prev.slice(-30), { id, emoji, left: 8 + Math.random() * 80 }]);
      setTimeout(() => setFloaters((prev) => prev.filter((f) => f.id !== id)), 3000);
      reactionTimesRef.current.push(Date.now());
      updateHype();
    });
    socket.on('live_signal', handleSignal);
    socket.on('live_error', ({ error }) => {
      toast.error(error);
      if (/not found|ended/i.test(error || '')) setTimeout(() => navigate('/live'), 1400);
    });
    socket.on('live_ended', () => {
      toast.info('This live has ended');
      navigate('/live');
    });

    const hypeTimer = setInterval(updateHype, 1000);

    return () => {
      clearInterval(hypeTimer);
      socket.emit('live_leave', {});
      socket.disconnect();
      Object.keys(peersRef.current).forEach(closePeer);
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useEffect(() => {
    // Scroll ONLY the chat box (scrollIntoView would drag the whole page down),
    // and only when the user is already near the bottom. When scrolled up,
    // count unseen messages for the "New messages ↓" chip instead.
    const box = chatBoxRef.current;
    if (!box) return;
    const added = messages.length - prevMsgCountRef.current;
    prevMsgCountRef.current = messages.length;
    const nearBottom = box.scrollHeight - box.scrollTop - box.clientHeight < 120;
    if (nearBottom) {
      box.scrollTop = box.scrollHeight;
      setUnseenChat(0);
    } else if (added > 0) {
      setUnseenChat((c) => c + added);
    }
  }, [messages]);

  const jumpToLatest = () => {
    const box = chatBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
    setUnseenChat(0);
  };

  const handleChatScroll = () => {
    const box = chatBoxRef.current;
    if (!box) return;
    if (box.scrollHeight - box.scrollTop - box.clientHeight < 40) setUnseenChat(0);
  };

  const me = roomState?.members.find((m) => m.sid === mySidRef.current);
  const isHost = roomState?.host_sid === mySidRef.current;
  const inBox = me?.box !== null && me?.box !== undefined;

  const takeBox = async (i) => {
    if (inBox) { emit('live_take_box', { box: i }); return; }
    const ok = await publishStream();
    if (ok) emit('live_take_box', { box: i });
  };

  const leaveBox = () => { emit('live_leave_box', {}); unpublishStream(); };

  const toggleMic = () => { localStreamRef.current?.getAudioTracks().forEach((t) => { t.enabled = !micOn; }); setMicOn(!micOn); };
  const toggleCam = () => { localStreamRef.current?.getVideoTracks().forEach((t) => { t.enabled = !camOn; }); setCamOn(!camOn); };

  const shareScreen = async () => {
    if (sharing) return stopShare();
    try {
      const ds = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const track = ds.getVideoTracks()[0];
      Object.values(peersRef.current).forEach(({ pc }) => {
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) sender.replaceTrack(track);
      });
      setLocalStream(new MediaStream([track, ...(localStreamRef.current?.getAudioTracks() || [])]));
      setSharing(true);
      track.onended = stopShare;
    } catch (e) { /* user cancelled */ }
  };

  const stopShare = () => {
    const camTrack = localStreamRef.current?.getVideoTracks()[0];
    Object.values(peersRef.current).forEach(({ pc }) => {
      const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender && camTrack) sender.replaceTrack(camTrack);
    });
    setLocalStream(localStreamRef.current);
    setSharing(false);
  };

  const sendChat = (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;
    emit('live_chat', { text: chatInput.trim() });
    setChatInput('');
  };

  const endOrLeave = () => {
    if (isHost) {
      if (!window.confirm('End this live for everyone? (Leaving without ending keeps it running for 5 minutes so you can come back)')) return;
      emit('live_end', { user_id: user?.user_id });
    } else {
      emit('live_leave', {});
    }
    navigate('/live');
  };

  if (!roomState) {
    return (
      <div className="relative z-10 min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
      </div>
    );
  }

  const cols = gridCols(roomState.max_boxes);
  const boxes = Array.from({ length: roomState.max_boxes }, (_, i) => roomState.members.find((m) => m.box === i) || null);

  return (
    <div className="relative z-10 min-h-screen pt-20 pb-4 px-2 sm:px-4" data-testid="live-room-page">
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-3 h-[calc(100vh-6.5rem)]">
        {/* Stage */}
        <div
          className={`relative overflow-hidden flex-1 rounded-2xl p-3 flex flex-col min-h-0 transition-shadow ${hypeLevel === 2 ? 'live-hype-2' : hypeLevel === 1 ? 'live-hype-1' : ''}`}
          style={roomState.cover
            ? { backgroundImage: `linear-gradient(rgba(8,6,20,0.55), rgba(8,6,20,0.72)), url(${roomState.cover})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { background: BG_PRESETS[roomState.background] || BG_PRESETS.midnight }}
          data-testid="live-stage"
        >
          {hypeLevel > 0 && (
            <span
              className={`absolute top-2 left-1/2 -translate-x-1/2 z-30 px-3 py-1 rounded-full text-xs font-extrabold tracking-wide ${
                hypeLevel === 2 ? 'bg-gradient-to-r from-amber-400 to-red-500 text-black' : 'bg-gradient-to-r from-fuchsia-500 to-purple-500 text-white'
              } animate-bounce`}
              data-testid="live-hype-badge"
            >
              {hypeLevel === 2 ? '🚀 MEGA HYPE!' : '🔥 HYPE!'}
            </span>
          )}
          {/* Floating reactions overlay */}
          <div className="absolute inset-0 pointer-events-none z-20" data-testid="live-reactions-overlay">
            {floaters.map((f) => (
              <span key={f.id} className="absolute bottom-16 text-3xl live-float-up" style={{ left: `${f.left}%` }}>
                {f.emoji}
              </span>
            ))}
          </div>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="flex items-center gap-1.5 bg-red-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full animate-pulse flex-shrink-0" data-testid="live-room-badge">
                <Radio className="w-3 h-3" /> LIVE
                <span className="flex items-center gap-0.5 border-l border-white/40 pl-1.5 font-semibold" data-testid="live-badge-viewers">
                  <Eye className="w-3 h-3" /> {roomState.members.filter((m) => m.box === null || m.box === undefined).length}
                </span>
              </span>
              <h1 className="text-white font-semibold truncate" data-testid="live-room-title">{roomState.title}</h1>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={async () => {
                  const url = `${window.location.origin}/live/${roomIdRef.current}`;
                  try {
                    if (navigator.share) {
                      await navigator.share({ title: roomState.title, text: `Watch "${roomState.title}" live!`, url });
                    } else {
                      await navigator.clipboard.writeText(url);
                      toast.success('Live link copied — share it anywhere!');
                    }
                  } catch (e) {
                    if (e?.name !== 'AbortError') {
                      try { await navigator.clipboard.writeText(url); toast.success('Live link copied!'); } catch (_) { toast.info(url); }
                    }
                  }
                }}
                className="flex items-center gap-1 bg-black/40 text-white text-xs px-2 py-1 rounded-full border border-white/20 hover:bg-black/60"
                data-testid="share-live-btn"
              >
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
              <span className="flex items-center gap-1 bg-black/40 text-white text-xs px-2 py-1 rounded-full" data-testid="live-viewer-count">
                <Eye className="w-3.5 h-3.5" /> {roomState.members.filter((m) => m.box === null || m.box === undefined).length} watching
              </span>
              {isHost && (
                <>
                  <select
                    value={roomState.max_boxes}
                    onChange={(e) => emit('live_set_layout', { max_boxes: parseInt(e.target.value) })}
                    className="bg-black/40 text-white text-xs rounded-full px-2 py-1 border border-white/20"
                    data-testid="live-layout-select"
                  >
                    {[1, 2, 3, 4, 6, 8, 9, 12].map((n) => <option key={n} value={n}>{n} box{n > 1 ? 'es' : ''}</option>)}
                  </select>
                  <button onClick={() => setShowBgPicker(!showBgPicker)} className="bg-black/40 text-white p-1.5 rounded-full border border-white/20" data-testid="live-bg-btn">
                    <Palette className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          {isHost && showBgPicker && (
            <div className="flex gap-2 mb-2 flex-wrap" data-testid="live-bg-picker">
              {Object.entries(BG_PRESETS).map(([id, css]) => (
                <button key={id} onClick={() => { emit('live_set_background', { background: id }); setShowBgPicker(false); }}
                  className={`w-8 h-8 rounded-md border-2 ${roomState.background === id ? 'border-white' : 'border-white/25'}`}
                  style={{ background: css }} data-testid={`live-setbg-${id}`} />
              ))}
            </div>
          )}

          {/* Video grid */}
          <div className="flex-1 grid gap-2 min-h-0" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }} data-testid="live-video-grid">
            {boxes.map((occupant, i) => (
              <div key={i} className="relative min-h-[100px]">
                {occupant ? (
                  <>
                    <VideoTile
                      stream={occupant.sid === mySidRef.current ? localStream : remoteStreams[occupant.sid]}
                      muted={occupant.sid === mySidRef.current}
                      mirrored={occupant.sid === mySidRef.current && mirrored && !sharing}
                      label={occupant.name}
                      isHost={occupant.sid === roomState.host_sid}
                    />
                    {occupant.sid === mySidRef.current && !isHost && (
                      <button onClick={leaveBox} className="absolute top-1.5 right-1.5 bg-black/60 p-1 rounded-full" data-testid="leave-box-btn">
                        <X className="w-3.5 h-3.5 text-white" />
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    onClick={() => takeBox(i)}
                    className="w-full h-full rounded-lg border-2 border-dashed border-white/25 hover:border-white/60 hover:bg-white/5 flex flex-col items-center justify-center gap-1 transition-colors"
                    data-testid={`join-box-${i}`}
                  >
                    <Video className="w-5 h-5 text-white/50" />
                    <span className="text-white/50 text-xs">{inBox ? 'Move here' : 'Join cam'}</span>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
            {/* Reactions — everyone can tap */}
            <div className="flex items-center gap-1 bg-black/35 rounded-full px-2 py-1" data-testid="live-reactions-bar">
              {['❤️', '🔥', '😂', '🎉', '👏', '😍'].map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => emit('live_reaction', { emoji })}
                  className="text-lg hover:scale-125 active:scale-95 transition-transform px-0.5"
                  data-testid={`live-react-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
            {inBox && (
              <>
                <Button size="sm" variant={micOn ? 'secondary' : 'destructive'} onClick={toggleMic} className="rounded-full" data-testid="toggle-mic-btn">
                  {micOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant={camOn ? 'secondary' : 'destructive'} onClick={toggleCam} className="rounded-full" data-testid="toggle-cam-btn">
                  {camOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
                </Button>
                <Button size="sm" variant={mirrored ? 'default' : 'secondary'} onClick={() => setMirrored(!mirrored)} className="rounded-full" data-testid="mirror-cam-btn" title="Mirror my camera (flip left/right)">
                  <FlipHorizontal2 className="w-4 h-4 mr-1" /> Mirror
                </Button>
                <Button size="sm" variant={sharing ? 'destructive' : 'secondary'} onClick={shareScreen} className="rounded-full" data-testid="share-screen-btn">
                  <MonitorUp className="w-4 h-4" />
                </Button>
              </>
            )}
            <Button size="sm" variant="destructive" onClick={endOrLeave} className="rounded-full" data-testid="leave-live-btn">
              <LogOut className="w-4 h-4 mr-1" /> {isHost ? 'End Live' : 'Leave'}
            </Button>
          </div>
        </div>

        {/* Chat */}
        <div className="lg:w-80 flex flex-col bg-black/40 border border-white/10 rounded-2xl p-3 min-h-[240px] lg:min-h-0" data-testid="live-chat-panel">
          <h3 className="text-white/80 text-sm font-semibold mb-2">Live chat</h3>
          <div className="flex-1 relative min-h-0">
            <div ref={chatBoxRef} onScroll={handleChatScroll} className="absolute inset-0 overflow-y-auto space-y-1.5 pr-1" style={{ scrollbarWidth: 'thin' }}>
              {messages.length === 0 && <p className="text-white/30 text-xs text-center pt-6">Say hi 👋</p>}
              {messages.map((m, i) => (
                <div key={i} className="text-sm leading-snug">
                  <span className={`font-semibold ${m.is_host ? 'text-red-400' : 'text-purple-300'}`}>{m.name}: </span>
                  <span className="text-white/85 break-words">{m.text}</span>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            {unseenChat > 0 && (
              <button
                onClick={jumpToLatest}
                data-testid="new-messages-chip"
                className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-purple-600 hover:bg-purple-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg animate-pulse"
              >
                <ArrowDown className="w-3.5 h-3.5" /> {unseenChat} new message{unseenChat > 1 ? 's' : ''}
              </button>
            )}
          </div>
          <form onSubmit={sendChat} className="flex gap-2 mt-2">
            <input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder="Send a message…"
              maxLength={300}
              className="flex-1 bg-white/10 text-white text-sm rounded-full px-3 py-2 outline-none border border-white/10 focus:border-purple-500"
              data-testid="live-chat-input"
            />
            <Button type="submit" size="sm" className="rounded-full bg-purple-600" data-testid="live-chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
